import { useState, useCallback, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Globe, Loader2, AlertCircle, Copy, Download, Trash2, Play, Pause, ArrowRight, ExternalLink, Wand2, ListChecks, Rocket } from 'lucide-react';

interface DomainResult {
  domain: string;
  available: boolean | null;
  registrar?: string;
  creationDate?: string;
  expirationDate?: string;
  error?: string;
}

interface DomainItem {
  keyword: string;
  suffix: string;
  domain: string;
  length: number;
  status?: 'pending' | 'checking' | 'available' | 'registered' | 'failed';
  result?: DomainResult;
}

interface AutoConfig {
  pattern: 'letters' | 'numbers' | 'mixed';
  minLength: number;
  maxLength: number;
  fixedKeyword: string;
  fixedPosition: 'prefix' | 'suffix' | 'contain';
  suffixes: string[];
  batchSize: number;
}

const SYSTEM_TLDS = ['.com', '.cn', '.top', '.org', '.work', '.ai', '.app', '.dev', '.vip'];

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const NUMBERS = '0123456789';

// 计算字符集大小
function getCharsetSize(pattern: 'letters' | 'numbers' | 'mixed'): number {
  switch (pattern) {
    case 'letters': return 26;
    case 'numbers': return 10;
    case 'mixed': return 36;
  }
}

// 计算给定规则下的所有域名组合数
function calculateCombinations(config: AutoConfig): number {
  const { pattern, minLength, maxLength, fixedKeyword, suffixes } = config;

  if (fixedKeyword) {
    let total = 0;
    const charsetSize = getCharsetSize(pattern);

    if (config.fixedPosition === 'prefix') {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len >= fixedLen) {
          const randomLen = len - fixedLen;
          total += Math.pow(charsetSize, randomLen);
        }
      }
    } else if (config.fixedPosition === 'suffix') {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len >= fixedLen) {
          const randomLen = len - fixedLen;
          total += Math.pow(charsetSize, randomLen);
        }
      }
    } else {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len >= fixedLen) {
          const randomTotal = len - fixedLen;
          for (let p = 0; p <= randomTotal; p++) {
            const s = randomTotal - p;
            total += Math.pow(charsetSize, p + s);
          }
        }
      }
    }
    return total * suffixes.length;
  } else {
    let total = 0;
    const charsetSize = getCharsetSize(pattern);
    for (let len = minLength; len <= maxLength; len++) {
      total += Math.pow(charsetSize, len);
    }
    return total * suffixes.length;
  }
}

// 生成指定范围内的所有域名
function generateAllDomains(config: AutoConfig): string[] {
  const domains: string[] = [];
  const { pattern, minLength, maxLength, fixedKeyword, fixedPosition, suffixes } = config;
  const charset = pattern === 'letters' ? LETTERS : pattern === 'numbers' ? NUMBERS : LETTERS + NUMBERS;

  if (fixedKeyword) {
    if (fixedPosition === 'prefix') {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len < fixedLen) continue;
        const randomLen = len - fixedLen;
        if (randomLen > 6) continue;

        const totalCombinations = Math.pow(charset.length, randomLen);
        for (let i = 0; i < totalCombinations; i++) {
          let randomPart = '';
          let temp = i;
          for (let j = 0; j < randomLen; j++) {
            randomPart = charset[temp % charset.length] + randomPart;
            temp = Math.floor(temp / charset.length);
          }
          const keyword = fixedKeyword + randomPart;
          suffixes.forEach(suffix => {
            domains.push(keyword + suffix);
          });
        }
      }
    } else if (fixedPosition === 'suffix') {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len < fixedLen) continue;
        const randomLen = len - fixedLen;
        if (randomLen > 6) continue;

        const totalCombinations = Math.pow(charset.length, randomLen);
        for (let i = 0; i < totalCombinations; i++) {
          let randomPart = '';
          let temp = i;
          for (let j = 0; j < randomLen; j++) {
            randomPart = charset[temp % charset.length] + randomPart;
            temp = Math.floor(temp / charset.length);
          }
          const keyword = randomPart + fixedKeyword;
          suffixes.forEach(suffix => {
            domains.push(keyword + suffix);
          });
        }
      }
    } else {
      for (let len = minLength; len <= maxLength; len++) {
        const fixedLen = fixedKeyword.length;
        if (len < fixedLen) continue;
        const randomTotal = len - fixedLen;
        if (randomTotal > 6) continue;

        for (let prefixLen = 0; prefixLen <= randomTotal; prefixLen++) {
          const suffixLen = randomTotal - prefixLen;
          const prefixComb = Math.pow(charset.length, prefixLen);
          const suffixComb = Math.pow(charset.length, suffixLen);

          for (let p = 0; p < prefixComb; p++) {
            let prefix = '';
            let tempP = p;
            for (let j = 0; j < prefixLen; j++) {
              prefix = charset[tempP % charset.length] + prefix;
              tempP = Math.floor(tempP / charset.length);
            }

            for (let s = 0; s < suffixComb; s++) {
              let suffix = '';
              let tempS = s;
              for (let j = 0; j < suffixLen; j++) {
                suffix = charset[tempS % charset.length] + suffix;
                tempS = Math.floor(tempS / charset.length);
              }

              const keyword = prefix + fixedKeyword + suffix;
              suffixes.forEach(suf => {
                domains.push(keyword + suf);
              });
            }
          }
        }
      }
    }
  } else {
    for (let len = minLength; len <= maxLength; len++) {
      if (len > 4) continue;
      const totalCombinations = Math.pow(charset.length, len);
      for (let i = 0; i < totalCombinations; i++) {
        let domain = '';
        let temp = i;
        for (let j = 0; j < len; j++) {
          domain = charset[temp % charset.length] + domain;
          temp = Math.floor(temp / charset.length);
        }
        suffixes.forEach(suffix => {
          domains.push(domain + suffix);
        });
      }
    }
  }

  return domains;
}

export default function App() {
  // 模式切换
  const [mode, setMode] = useState<'manual' | 'auto'>('manual');

  // 模块1：域名关键字（手动模式）
  const [keywordsInput, setKeywordsInput] = useState('');

  // 模块2：域名后缀
  const [systemTlds, setSystemTlds] = useState<string[]>(['.com', '.cn', '.top', '.org']);
  const [customSuffix, setCustomSuffix] = useState('');

  // 自动模式配置
  const [autoConfig, setAutoConfig] = useState<AutoConfig>({
    pattern: 'letters',
    minLength: 4,
    maxLength: 4,
    fixedKeyword: 'fj',
    fixedPosition: 'prefix',
    suffixes: ['.com'],
    batchSize: 100,
  });

  // 批次信息
  const [batchInfo, setBatchInfo] = useState<{
    totalDomains: string[];
    totalBatches: number;
    currentBatch: number;
  } | null>(null);

  // 模块3：查询列表
  const [domainList, setDomainList] = useState<DomainItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'registered' | 'pending' | 'failed'>('all');

  // 暂停状态
  const [isPaused, setIsPaused] = useState(false);
  const pausedRef = useRef(false);

  // 查询所有批次相关
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [allResults, setAllResults] = useState<DomainItem[]>([]);
  const [checkingBatchIndex, setCheckingBatchIndex] = useState(0);
  const allResultsRef = useRef<DomainItem[]>([]);

  // 解析关键词列表（手动模式）
  const keywords = useMemo(() => {
    return keywordsInput
      .split('\n')
      .map(k => k.trim().toLowerCase().replace(/[^a-z0-9]/g, ''))
      .filter(k => k);
  }, [keywordsInput]);

  // 所有可选后缀
  const allSuffixes = useMemo(() => {
    const custom = customSuffix
      .split(/[,\n]/)
      .map(s => s.trim().toLowerCase())
      .filter(s => s && !s.startsWith('.'))
      .map(s => s.startsWith('.') ? s : `.${s}`);
    return [...new Set([...systemTlds, ...custom])];
  }, [systemTlds, customSuffix]);

  // 预估域名数量（手动模式）
  const estimatedManualCount = useMemo(() => {
    return keywords.length * allSuffixes.length;
  }, [keywords, allSuffixes]);

  // 计算自动模式的组合数
  const autoCombinations = useMemo(() => {
    return calculateCombinations(autoConfig);
  }, [autoConfig]);

  // 总批次数
  const totalBatches = useMemo(() => {
    if (mode === 'auto') {
      return Math.ceil(autoCombinations / autoConfig.batchSize);
    }
    return 1;
  }, [mode, autoCombinations, autoConfig.batchSize]);

  // 生成手动模式域名列表
  const generateManualDomainList = useCallback(() => {
    const items: DomainItem[] = [];

    keywords.forEach(keyword => {
      allSuffixes.forEach(suffix => {
        const domain = `${keyword}${suffix}`;
        items.push({
          keyword,
          suffix,
          domain,
          length: domain.length,
          status: 'pending',
        });
      });
    });

    setDomainList(items);
    setBatchInfo(null);
    setAllResults([]);
  }, [keywords, allSuffixes]);

  // 生成自动模式域名
  const generateAutoDomains = useCallback(() => {
    const allDomains = generateAllDomains(autoConfig);

    const batches: string[][] = [];
    for (let i = 0; i < allDomains.length; i += autoConfig.batchSize) {
      batches.push(allDomains.slice(i, i + autoConfig.batchSize));
    }

    setBatchInfo({
      totalDomains: allDomains,
      totalBatches: batches.length,
      currentBatch: 0,
    });

    const firstBatch = batches[0] || [];
    const items: DomainItem[] = firstBatch.map(domain => {
      const lastDotIndex = domain.lastIndexOf('.');
      const keyword = domain.substring(0, lastDotIndex);
      const suffix = domain.substring(lastDotIndex);
      return {
        keyword,
        suffix,
        domain,
        length: domain.length,
        status: 'pending' as const,
      };
    });

    setDomainList(items);
    setAllResults([]);
    setIsCheckingAll(false);
  }, [autoConfig]);

  // 加载指定批次
  const loadBatch = useCallback((batchIndex: number) => {
    if (!batchInfo) return;

    // 如果有全部查询结果，从结果中加载该批次
    if (allResultsRef.current.length > 0) {
      const start = batchIndex * autoConfig.batchSize;
      const end = start + autoConfig.batchSize;
      const batchResults = allResultsRef.current.slice(start, end);
      
      if (batchResults.length > 0) {
        setDomainList(batchResults);
        setBatchInfo(prev => prev ? { ...prev, currentBatch: batchIndex } : null);
        setFilterStatus('all');
        return;
      }
    }

    // 否则从总域名列表加载
    const start = batchIndex * autoConfig.batchSize;
    const end = start + autoConfig.batchSize;
    const batch = batchInfo.totalDomains.slice(start, end);

    const items: DomainItem[] = batch.map(domain => {
      const lastDotIndex = domain.lastIndexOf('.');
      const keyword = domain.substring(0, lastDotIndex);
      const suffix = domain.substring(lastDotIndex);
      return {
        keyword,
        suffix,
        domain,
        length: domain.length,
        status: 'pending' as const,
      };
    });

    setDomainList(items);
    setBatchInfo(prev => prev ? { ...prev, currentBatch: batchIndex } : null);
    setFilterStatus('all');
  }, [batchInfo, autoConfig.batchSize]);

  // 切换系统后缀
  const toggleSystemTld = (tld: string) => {
    setSystemTlds(prev =>
      prev.includes(tld)
        ? prev.filter(t => t !== tld)
        : [...prev, tld]
    );
  };

  // 选择所有系统后缀
  const selectAllSystemTlds = () => {
    setSystemTlds([...SYSTEM_TLDS]);
  };

  // 清除所有系统后缀
  const clearAllSystemTlds = () => {
    setSystemTlds([]);
  };

  // 清空查询列表
  const clearDomainList = () => {
    setDomainList([]);
    setBatchInfo(null);
    setProgress(0);
    setFilterStatus('all');
    setIsPaused(false);
    setIsCheckingAll(false);
    setAllResults([]);
    setCheckingBatchIndex(0);
    allResultsRef.current = [];
    pausedRef.current = false;
  };

  // 开始查询单批次
  const startCheck = useCallback(async () => {
    if (domainList.length === 0) return;

    setLoading(true);
    setProgress(0);
    pausedRef.current = false;
    setIsPaused(false);

    setDomainList(prev => prev.map(item => ({ ...item, status: 'checking' })));

    const domains = domainList.map(item => item.domain);

    try {
      const response = await fetch('/api/domain-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domains }),
      });

      if (!response.ok) {
        throw new Error('查询请求失败');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('无法读取响应');
      }

      const resultsMap = new Map<string, DomainResult>();

      while (true) {
        // 检查是否暂停
        if (pausedRef.current) {
          await new Promise(resolve => {
            const checkPause = setInterval(() => {
              if (!pausedRef.current) {
                clearInterval(checkPause);
                resolve(undefined);
              }
            }, 100);
          });
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'result') {
                resultsMap.set(data.result.domain, data.result);
              } else if (data.type === 'progress') {
                setProgress(data.percent);
              }
            } catch {
              // 忽略解析错误
            }
          }
        }
      }

      setDomainList(prev => prev.map(item => {
        const result = resultsMap.get(item.domain);
        if (result) {
          return {
            ...item,
            status: result.available === true ? 'available' :
              result.available === false ? 'registered' : 'failed',
            result,
          };
        }
        return { ...item, status: 'failed' };
      }));
    } catch (error) {
      console.error('查询失败:', error);
      setDomainList(prev => prev.map(item =>
        item.status === 'checking' ? { ...item, status: 'failed' } : item
      ));
    } finally {
      setLoading(false);
      setProgress(100);
    }
  }, [domainList]);

  // 暂停/继续查询
  const togglePause = () => {
    pausedRef.current = !pausedRef.current;
    setIsPaused(!isPaused);
  };

  // 查询所有批次
  const startCheckAllBatches = useCallback(async () => {
    if (!batchInfo) return;

    setIsCheckingAll(true);
    setIsPaused(false);
    pausedRef.current = false;
    allResultsRef.current = [];
    setAllResults([]);
    setCheckingBatchIndex(0);

    const allDomains = batchInfo.totalDomains;
    const batchSize = autoConfig.batchSize;
    const totalBatchCount = Math.ceil(allDomains.length / batchSize);
    const completedResults: DomainItem[] = [];

    for (let batchIdx = 0; batchIdx < totalBatchCount; batchIdx++) {
      // 检查是否暂停
      while (pausedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setCheckingBatchIndex(batchIdx);

      // 提取当前批次的域名
      const start = batchIdx * batchSize;
      const end = Math.min(start + batchSize, allDomains.length);
      const batchDomains = allDomains.slice(start, end);

      // 初始化当前批次的 DomainItem
      const batchItems: DomainItem[] = batchDomains.map(domain => {
        const lastDotIndex = domain.lastIndexOf('.');
        const keyword = domain.substring(0, lastDotIndex);
        const suffix = domain.substring(lastDotIndex);
        return {
          keyword,
          suffix,
          domain,
          length: domain.length,
          status: 'checking' as const,
        };
      });

      try {
        const response = await fetch('/api/domain-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domains: batchDomains }),
        });

        if (!response.ok) {
          throw new Error('查询请求失败');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        if (!reader) {
          throw new Error('无法读取响应');
        }

        const resultsMap = new Map<string, DomainResult>();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'result') {
                  resultsMap.set(data.result.domain, data.result);
                }
              } catch {
                // 忽略解析错误
              }
            }
          }
        }

        // 更新当前批次结果
        const batchResults = batchItems.map(item => {
          const result = resultsMap.get(item.domain);
          if (result) {
            return {
              ...item,
              status: (result.available === true ? 'available' :
                result.available === false ? 'registered' : 'failed') as 'available' | 'registered' | 'failed',
              result,
            };
          }
          return { ...item, status: 'failed' as const };
        });

        completedResults.push(...batchResults);
        allResultsRef.current = [...completedResults];
        setAllResults([...completedResults]);

        // 更新当前显示
        if (batchIdx === batchInfo.currentBatch) {
          setDomainList(batchResults);
        }

        // 更新进度
        setProgress(Math.round(((batchIdx + 1) / totalBatchCount) * 100));

      } catch (error) {
        console.error(`批次 ${batchIdx} 查询失败:`, error);
        // 即使失败，也添加到结果中
        const failedResults = batchItems.map(item => ({
          ...item,
          status: 'failed' as const,
        }));
        completedResults.push(...failedResults);
        allResultsRef.current = [...completedResults];
        setAllResults([...completedResults]);
      }
    }

    setIsCheckingAll(false);
    setProgress(100);
  }, [batchInfo, autoConfig.batchSize]);

  // 停止查询所有
  const stopCheckAll = () => {
    pausedRef.current = true;
    setIsPaused(true);
    setIsCheckingAll(false);
  };

  // 复制可用域名
  const copyAvailableDomains = () => {
    const listToUse = allResults.length > 0 ? allResults : domainList;
    const available = listToUse
      .filter(item => item.status === 'available')
      .map(item => item.domain)
      .join('\n');
    navigator.clipboard.writeText(available);
  };

  // 导出结果
  const exportResults = () => {
    const listToUse = allResults.length > 0 ? allResults : domainList;
    const csv = [
      '序号,域名,长度,到期时间,WHOIS链接,查询结果',
      ...listToUse.map((item, idx) => [
        idx + 1,
        item.domain,
        item.length,
        item.result?.expirationDate ? new Date(item.result.expirationDate).toLocaleDateString('zh-CN') : '',
        `https://whois.domaintools.com/${item.domain}`,
        item.status === 'available' ? '可注册' :
          item.status === 'registered' ? '已注册' :
            item.status === 'checking' ? '查询中' :
              item.status === 'failed' ? '失败' : '待查询',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'domain-check-results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // 统计
  const stats = useMemo(() => {
    const listToUse = allResults.length > 0 ? allResults : domainList;
    return {
      total: listToUse.length,
      available: listToUse.filter(d => d.status === 'available').length,
      registered: listToUse.filter(d => d.status === 'registered').length,
      pending: listToUse.filter(d => d.status === 'pending' || d.status === 'checking').length,
      failed: listToUse.filter(d => d.status === 'failed').length,
    };
  }, [domainList, allResults]);

  // 过滤后的数据
  const filteredDomainList = useMemo(() => {
    const listToUse = allResults.length > 0 ? allResults : domainList;
    if (filterStatus === 'all') return listToUse;
    if (filterStatus === 'available') return listToUse.filter(d => d.status === 'available');
    if (filterStatus === 'registered') return listToUse.filter(d => d.status === 'registered');
    if (filterStatus === 'pending') return listToUse.filter(d => d.status === 'pending' || d.status === 'checking');
    if (filterStatus === 'failed') return listToUse.filter(d => d.status === 'failed');
    return listToUse;
  }, [domainList, allResults, filterStatus]);

  // 总进度（查询所有批次时）
  const totalProgress = useMemo(() => {
    if (!batchInfo || !isCheckingAll) return 0;
    return Math.round((checkingBatchIndex / batchInfo.totalBatches) * 100);
  }, [batchInfo, isCheckingAll, checkingBatchIndex]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <Globe className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white">
              域名查查查
            </h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            批量检查域名注册状态，快速找到可注册的优质域名
          </p>
        </div>

        {/* 模式切换 */}
        <div className="flex justify-center">
          <RadioGroup value={mode} onValueChange={(v) => { setMode(v as 'manual' | 'auto'); clearDomainList(); }} className="flex gap-4">
            <div className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-all ${
              mode === 'manual'
                ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500'
                : 'bg-slate-100 dark:bg-slate-800 border-2 border-transparent hover:border-slate-300'
            }`}>
              <RadioGroupItem value="manual" id="manual" className="sr-only" />
              <Label htmlFor="manual" className="cursor-pointer flex items-center gap-2">
                <ListChecks className="w-5 h-5" />
                手动模式
              </Label>
            </div>
            <div className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-all ${
              mode === 'auto'
                ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                : 'bg-slate-100 dark:bg-slate-800 border-2 border-transparent hover:border-slate-300'
            }`}>
              <RadioGroupItem value="auto" id="auto" className="sr-only" />
              <Label htmlFor="auto" className="cursor-pointer flex items-center gap-2">
                <Wand2 className="w-5 h-5" />
                自动遍历
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* 手动模式 */}
        {mode === 'manual' && (
          <>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-sm font-bold">1</span>
                  域名关键字输入
                </CardTitle>
                <CardDescription>
                  输入品牌/产品关键词，每行一个（只保留字母和数字）
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={keywordsInput}
                  onChange={e => setKeywordsInput(e.target.value)}
                  placeholder="mybrand&#10;mycompany&#10;techstart&#10;cloudapp&#10;..."
                  className="w-full h-32 p-3 border rounded-lg bg-background resize-none font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <div className="flex items-center justify-end mt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setKeywordsInput('')}
                    disabled={!keywordsInput.trim()}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    清空
                  </Button>
                  <span className="text-sm text-slate-500 ml-4">
                    {keywords.length} 个关键词
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-sm font-bold">2</span>
                  域名后缀选择
                </CardTitle>
                <CardDescription>
                  选择系统后缀或自定义后缀（用逗号分隔）
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      系统后缀：
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={selectAllSystemTlds}>
                        全选
                      </Button>
                      <Button size="sm" variant="ghost" onClick={clearAllSystemTlds}>
                        清空
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SYSTEM_TLDS.map(tld => (
                      <Badge
                        key={tld}
                        variant={systemTlds.includes(tld) ? 'default' : 'outline'}
                        className="cursor-pointer text-sm px-3 py-1"
                        onClick={() => toggleSystemTld(tld)}
                      >
                        {tld}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    自定义后缀：
                  </span>
                  <Input
                    placeholder="输入自定义后缀，如：.online, .site, .tech（逗号分隔）"
                    value={customSuffix}
                    onChange={e => setCustomSuffix(e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    已选择 {allSuffixes.length} 个后缀，预计生成 {estimatedManualCount} 个域名
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button
                onClick={generateManualDomainList}
                disabled={keywords.length === 0 || allSuffixes.length === 0}
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 px-8"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                生成查询列表
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* 自动遍历模式 */}
        {mode === 'auto' && (
          <>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-600" />
                  遍历规则配置
                </CardTitle>
                <CardDescription>
                  配置域名生成规则，系统将根据规则遍历所有组合
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    域名构成模式：
                  </span>
                  <RadioGroup
                    value={autoConfig.pattern}
                    onValueChange={(v) => setAutoConfig(prev => ({ ...prev, pattern: v as 'letters' | 'numbers' | 'mixed' }))}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="letters" id="letters" />
                      <Label htmlFor="letters" className="cursor-pointer">纯字母 (a-z, 26种)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="numbers" id="numbers" />
                      <Label htmlFor="numbers" className="cursor-pointer">纯数字 (0-9, 10种)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="mixed" id="mixed" />
                      <Label htmlFor="mixed" className="cursor-pointer">字母+数字 (36种)</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      最小位数：
                    </span>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={autoConfig.minLength}
                      onChange={(e) => setAutoConfig(prev => ({ ...prev, minLength: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      最大位数：
                    </span>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={autoConfig.maxLength}
                      onChange={(e) => setAutoConfig(prev => ({ ...prev, maxLength: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      固定关键字：
                    </span>
                    <Input
                      placeholder="如：fj, abc, 123（留空则全部随机）"
                      value={autoConfig.fixedKeyword}
                      onChange={(e) => setAutoConfig(prev => ({ ...prev, fixedKeyword: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      关键字位置：
                    </span>
                    <RadioGroup
                      value={autoConfig.fixedPosition}
                      onValueChange={(v) => setAutoConfig(prev => ({ ...prev, fixedPosition: v as 'prefix' | 'suffix' | 'contain' }))}
                      className="flex gap-3"
                    >
                      <div className="flex items-center gap-1">
                        <RadioGroupItem value="prefix" id="prefix" />
                        <Label htmlFor="prefix" className="cursor-pointer text-sm">前缀</Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <RadioGroupItem value="suffix" id="suffix" />
                        <Label htmlFor="suffix" className="cursor-pointer text-sm">后缀</Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <RadioGroupItem value="contain" id="contain" />
                        <Label htmlFor="contain" className="cursor-pointer text-sm">包含</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    域名后缀：
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {SYSTEM_TLDS.map(tld => (
                      <Badge
                        key={tld}
                        variant={autoConfig.suffixes.includes(tld) ? 'default' : 'outline'}
                        className="cursor-pointer text-sm px-3 py-1"
                        onClick={() => {
                          setAutoConfig(prev => ({
                            ...prev,
                            suffixes: prev.suffixes.includes(tld)
                              ? prev.suffixes.filter(s => s !== tld)
                              : [...prev.suffixes, tld]
                          }));
                        }}
                      >
                        {tld}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      每批域名数量：
                    </span>
                    <Input
                      type="number"
                      min={10}
                      max={500}
                      value={autoConfig.batchSize}
                      onChange={(e) => setAutoConfig(prev => ({ ...prev, batchSize: parseInt(e.target.value) || 100 }))}
                      className="w-32"
                    />
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      总批次数：
                    </span>
                    <p className="text-lg font-bold text-purple-600">
                      {totalBatches} 批
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg space-y-2">
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-400">
                    组合计算：
                  </span>
                  {autoConfig.fixedKeyword ? (
                    <div className="text-sm text-purple-600 dark:text-purple-300 space-y-1">
                      <p>- 模式：{autoConfig.pattern === 'letters' ? '纯字母(26)' : autoConfig.pattern === 'numbers' ? '纯数字(10)' : '混合(36)'}</p>
                      <p>- 固定关键字：{autoConfig.fixedKeyword}（{autoConfig.fixedKeyword.length}位，{autoConfig.fixedPosition === 'prefix' ? '作为前缀' : autoConfig.fixedPosition === 'suffix' ? '作为后缀' : '包含在中间'}）</p>
                      <p>- 位数范围：{autoConfig.minLength}-{autoConfig.maxLength}位</p>
                      <p>- 后缀数量：{autoConfig.suffixes.length} 个</p>
                      <p className="font-bold pt-1">
                        总组合数：{autoCombinations.toLocaleString()} 个域名
                        {autoCombinations > 50000 && <span className="text-amber-600 ml-2">（组合较多，请耐心等待生成）</span>}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-purple-600 dark:text-purple-300 space-y-1">
                      <p>- 模式：{autoConfig.pattern === 'letters' ? '纯字母(26)' : autoConfig.pattern === 'numbers' ? '纯数字(10)' : '混合(36)'}</p>
                      <p>- 无固定关键字（全部随机）</p>
                      <p>- 位数范围：{autoConfig.minLength}-{autoConfig.maxLength}位</p>
                      <p>- 后缀数量：{autoConfig.suffixes.length} 个</p>
                      <p className="font-bold pt-1">
                        总组合数：{autoCombinations.toLocaleString()} 个域名
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button
                onClick={generateAutoDomains}
                disabled={autoConfig.suffixes.length === 0 || autoCombinations === 0}
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 px-8"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                遍历所有 {autoCombinations.toLocaleString()} 个域名组合
              </Button>
            </div>

            {/* 批次选择 */}
            {batchInfo && totalBatches > 1 && (
              <Card className="shadow-lg border-purple-200 dark:border-purple-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    批次导航（共 {batchInfo.totalBatches} 批 / {autoCombinations.toLocaleString()} 个域名）
                    {allResults.length > 0 && (
                      <Badge variant="outline" className="ml-2 bg-green-100 text-green-700 border-green-300">
                        已查询 {allResults.length} 个
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: Math.min(batchInfo.totalBatches, 15) }).map((_, idx) => (
                      <Button
                        key={idx}
                        variant={batchInfo.currentBatch === idx ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => loadBatch(idx)}
                        className={batchInfo.currentBatch === idx ? 'bg-purple-600' : ''}
                      >
                        {idx * autoConfig.batchSize + 1}-{Math.min((idx + 1) * autoConfig.batchSize, autoCombinations)}
                      </Button>
                    ))}
                    {batchInfo.totalBatches > 15 && (
                      <span className="text-sm text-slate-500 self-center ml-2">
                        ... 还有 {batchInfo.totalBatches - 15} 批
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    当前显示：第 {batchInfo.currentBatch + 1} 批
                    （{batchInfo.currentBatch * autoConfig.batchSize + 1}-{Math.min((batchInfo.currentBatch + 1) * autoConfig.batchSize, autoCombinations)} 个域名）
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* 模块3：查询列表 */}
        {domainList.length > 0 && (
          <>
            <Card className="shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-sm font-bold">3</span>
                    查询列表
                    {batchInfo && (
                      <Badge variant="outline" className="ml-2">
                        第 {batchInfo.currentBatch + 1}/{batchInfo.totalBatches} 批
                      </Badge>
                    )}
                    {allResults.length > 0 && (
                      <Badge variant="outline" className="ml-2 bg-blue-100 text-blue-700 border-blue-300">
                        全部 {allResults.length} 个
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearDomainList}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      清空
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyAvailableDomains}
                      disabled={stats.available === 0}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      复制可用 ({stats.available})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportResults}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      导出
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 统计卡片 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-slate-600 dark:text-slate-400">数据一览：</span>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      filterStatus === 'all'
                        ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    <p className={`text-2xl font-bold ${filterStatus === 'all' ? 'text-white' : 'text-slate-600'}`}>{stats.total}</p>
                    <p className={`text-xs ${filterStatus === 'all' ? 'text-blue-100' : 'text-slate-500'}`}>总计</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus('available')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      filterStatus === 'available'
                        ? 'bg-green-600 text-white ring-2 ring-green-400'
                        : 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'
                    }`}
                  >
                    <p className={`text-2xl font-bold ${filterStatus === 'available' ? 'text-white' : 'text-green-600'}`}>{stats.available}</p>
                    <p className={`text-xs ${filterStatus === 'available' ? 'text-green-100' : 'text-green-600'}`}>可注册</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus('registered')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      filterStatus === 'registered'
                        ? 'bg-red-600 text-white ring-2 ring-red-400'
                        : 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50'
                    }`}
                  >
                    <p className={`text-2xl font-bold ${filterStatus === 'registered' ? 'text-white' : 'text-red-600'}`}>{stats.registered}</p>
                    <p className={`text-xs ${filterStatus === 'registered' ? 'text-red-100' : 'text-red-600'}`}>已注册</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus('pending')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      filterStatus === 'pending'
                        ? 'bg-amber-600 text-white ring-2 ring-amber-400'
                        : 'bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                    }`}
                  >
                    <p className={`text-2xl font-bold ${filterStatus === 'pending' ? 'text-white' : 'text-amber-600'}`}>{stats.pending}</p>
                    <p className={`text-xs ${filterStatus === 'pending' ? 'text-amber-100' : 'text-amber-600'}`}>待查询</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus('failed')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      filterStatus === 'failed'
                        ? 'bg-slate-600 text-white ring-2 ring-slate-400'
                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    <p className={`text-2xl font-bold ${filterStatus === 'failed' ? 'text-white' : 'text-slate-600'}`}>{stats.failed}</p>
                    <p className={`text-xs ${filterStatus === 'failed' ? 'text-slate-300' : 'text-slate-500'}`}>失败</p>
                  </button>
                </div>
                {filterStatus !== 'all' && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      当前筛选：{filteredDomainList.length} 条结果
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => setFilterStatus('all')}>
                      显示全部
                    </Button>
                  </div>
                )}

                {/* 进度条 - 查询所有批次时显示总进度 */}
                {(loading || isCheckingAll) && (
                  <Progress value={isCheckingAll ? totalProgress : progress} className="h-2" />
                )}

                {/* 查询所有批次状态 */}
                {isCheckingAll && (
                  <div className="flex items-center justify-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                    <span className="text-purple-700 dark:text-purple-400">
                      正在查询全部批次... 第 {checkingBatchIndex + 1}/{batchInfo?.totalBatches || 0} 批 ({totalProgress}%)
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={stopCheckAll}
                      className="ml-2"
                    >
                      <Pause className="w-4 h-4 mr-1" />
                      暂停
                    </Button>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex justify-center gap-4 flex-wrap">
                  {/* 单批次操作 */}
                  {!isCheckingAll && (
                    <>
                      {batchInfo && batchInfo.currentBatch > 0 && (
                        <Button
                          onClick={() => loadBatch(batchInfo.currentBatch - 1)}
                          variant="outline"
                          size="lg"
                          disabled={loading}
                        >
                          上一批
                        </Button>
                      )}

                      <Button
                        onClick={startCheck}
                        disabled={loading || stats.pending === 0}
                        size="lg"
                        className="bg-blue-600 hover:bg-blue-700 px-8"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {isPaused ? '已暂停' : `查询中... ${progress}%`}
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            查询当前批
                          </>
                        )}
                      </Button>

                      {loading && (
                        <Button
                          onClick={togglePause}
                          variant="outline"
                          size="lg"
                          className="px-6"
                        >
                          {isPaused ? (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              继续
                            </>
                          ) : (
                            <>
                              <Pause className="w-4 h-4 mr-1" />
                              暂停
                            </>
                          )}
                        </Button>
                      )}

                      {batchInfo && batchInfo.currentBatch < batchInfo.totalBatches - 1 && (
                        <Button
                          onClick={() => loadBatch(batchInfo.currentBatch + 1)}
                          variant="outline"
                          size="lg"
                          disabled={loading}
                        >
                          下一批
                        </Button>
                      )}

                      {/* 查询所有批次按钮 */}
                      {batchInfo && batchInfo.totalBatches > 1 && (
                        <Button
                          onClick={startCheckAllBatches}
                          variant="default"
                          size="lg"
                          disabled={loading}
                          className="bg-green-600 hover:bg-green-700 px-6"
                        >
                          <Rocket className="w-4 h-4 mr-2" />
                          查询全部 {batchInfo.totalBatches} 批
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {/* 表格 */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                        <tr>
                          <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-400 w-14">序号</th>
                          <th className="px-3 py-3 text-left font-medium text-slate-600 dark:text-slate-400">域名</th>
                          <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400 w-16">长度</th>
                          <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400 w-28">到期时间</th>
                          <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400 w-28">WHOIS</th>
                          <th className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400 w-28">查询结果</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {filteredDomainList.map((item, idx) => (
                          <tr key={`${item.domain}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <span className="font-mono">{item.domain}</span>
                            </td>
                            <td className="px-3 py-2 text-center text-slate-500">{item.length}</td>
                            <td className="px-3 py-2 text-center text-sm text-slate-600 dark:text-slate-400">
                              {item.status === 'registered' && item.result?.expirationDate ? (
                                new Date(item.result.expirationDate).toLocaleDateString('zh-CN')
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center text-sm text-slate-600 dark:text-slate-400 truncate max-w-28">
                              {item.status === 'registered' && item.result?.registrar ? (
                                <a
                                  href={`https://whois.domaintools.com/${item.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  WHOIS
                                </a>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {item.status === 'pending' && (
                                <Badge variant="outline" className="text-slate-500">待查询</Badge>
                              )}
                              {item.status === 'checking' && (
                                <Badge variant="outline" className="text-amber-600">
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  查询中
                                </Badge>
                              )}
                              {item.status === 'available' && (
                                <Badge variant="default" className="bg-green-600 text-white">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  可注册
                                </Badge>
                              )}
                              {item.status === 'registered' && (
                                <Badge variant="destructive">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  已注册
                                </Badge>
                              )}
                              {item.status === 'failed' && (
                                <div className="flex flex-col items-center">
                                  <Badge variant="outline" className="text-red-500">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    查询失败
                                  </Badge>
                                  {item.result?.error && (
                                    <span className="text-xs text-slate-500 mt-1" title={item.result.error}>
                                      {item.result.error}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          支持主流顶级域名 (.com, .cn, .top, .org, .work, .ai, .app, .dev, .vip 等)
        </div>
      </div>
    </div>
  );
}
